import { create } from "zustand";
import { RootState, INITIAL_STATE } from "../core/state-shape";
import { http } from "../lib/http";

import { toast } from "sonner";
export type RepoState = RootState["repo"];

type RepoActions = {
  setState: (newState: RepoState) => void;
  patch: (p: Partial<RepoState>) => void;
  reset: () => void;

  connect: (body: { provider: string; token: string }) => Promise<void>;
  listRepos: (org: string) => Promise<void>;
  listBranches: (org: string, repo: string) => Promise<void>;
  selectOrg: (org: string) => void;
  selectRepo: (repo: string) => void;
  selectBranch: (branch: string) => void;
};

export const useRepoStore = create<RepoState & RepoActions>((set, get) => ({
  ...INITIAL_STATE.repo,
  setState: (newState) => set(newState),
  patch: (p) => set(p),
  reset: () => set({ ...INITIAL_STATE.repo }),

  async connect(body) {
    try {
      const res = await http<{ success: boolean; orgs: Array<{ id: string; name: string; type: string }> }>("/api/repo/connect", {
        method: "POST",
        body: JSON.stringify(body),
      });
      // keep it simple: update only what we know exists on this slice
      set((s) => ({
        connectionStatus: "connected",
        permissions: { ...(s.permissions || {}), read: true, write: true },
        orgsAvailable: res.orgs,
        connectedAt: new Date().toISOString(),
      } as Partial<RepoState>));
      toast.success("Connected to repository provider successfully!");
    } catch (e) {
      console.error("Failed to connect to repo provider", e);
      toast.error("Failed to connect. Please check your token and try again.");
      throw e;
    }
  },

  async listRepos(org) {
    const res = await http<{ repos: any[] }>(`/api/repo/${encodeURIComponent(org)}/repos`);
    set({ repos: res.repos } as Partial<RepoState>);
  },

  async listBranches(org, repo) {
    const res = await http<{ branches: any[] }>(
      `/api/repo/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/branches`
    );
    set({ branches: res.branches } as Partial<RepoState>);
  },

  selectOrg: (org) => set({ selectedOrg: org } as Partial<RepoState>),
  selectRepo: (repo) => set({ selectedRepo: repo } as Partial<RepoState>),
  selectBranch: (branch) => set({ branch } as Partial<RepoState>),
}));